import { memo, useCallback, useEffect, useState, type ChangeEvent } from 'react'
import { AlertTriangle, Film, Monitor } from 'lucide-react'
import { adTransitionApi, type AdTransitionSettings } from '../../lib/adsApi.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { Spinner } from '../common/Spinner.js'

interface Props {
  channelId: string | null
  canManage: boolean
}

function formatMilliseconds(durationMs: number): string {
  return durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toLocaleString('fr-FR')} s`
}

export const AdTransitionSettingsTab = memo(function AdTransitionSettingsTab({ channelId, canManage }: Props) {
  const [settings, setSettings] = useState<AdTransitionSettings | null>(null)
  const [mode, setMode] = useState<'BLACK' | 'VIDEO'>('BLACK')
  const [blackDurationMs, setBlackDurationMs] = useState(1000)
  const [selectedMedia, setSelectedMedia] = useState<ProbeResult | null>(null)
  const [browserOpen, setBrowserOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [success, setSuccess] = useState('')

  const load = useCallback(async (syncForm = false) => {
    if (!channelId) return
    try {
      const next = await adTransitionApi.get(channelId)
      setSettings(next)
      if (syncForm) {
        setMode(next.mode)
        setBlackDurationMs(next.blackDurationMs)
      }
      setLoadError('')
    } catch (err: any) {
      setLoadError(err?.message ?? 'Réglage indisponible')
    }
  }, [channelId])

  useEffect(() => {
    if (!channelId) { setSettings(null); return }
    setSelectedMedia(null)
    setBrowserOpen(false)
    setActionError('')
    setSuccess('')
    setLoading(true)
    load(true).finally(() => setLoading(false))
    const timer = window.setInterval(() => { void load(false) }, 5000)
    return () => window.clearInterval(timer)
  }, [channelId, load])

  const handleModeBlack = useCallback(() => setMode('BLACK'), [])
  const handleModeVideo = useCallback(() => setMode('VIDEO'), [])
  const openBrowser = useCallback(() => setBrowserOpen(true), [])
  const closeBrowser = useCallback(() => setBrowserOpen(false), [])
  const handleDurationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setBlackDurationMs(Math.min(60_000, Math.max(0, Number(event.target.value) || 0)))
  }, [])
  const handleMediaSelect = useCallback((result: ProbeResult) => {
    if (!result.absolutePath || !result.durationSec || result.status === 'ERROR') {
      setActionError(result.error ?? 'Le média sélectionné n’est pas une vidéo exploitable.')
      return
    }
    setSelectedMedia(result)
    setMode('VIDEO')
    setBrowserOpen(false)
    setActionError('')
    setSuccess('')
  }, [])

  const saveResult = useCallback((next: AdTransitionSettings) => {
    setSettings(next)
    setMode(next.mode)
    setBlackDurationMs(next.blackDurationMs)
    setSelectedMedia(null)
    setSuccess(`${next.updatedBlocks ?? 0} bloc(s) futur(s) recalculé(s) dans la grille.`)
    setActionError('')
  }, [])

  const handleSave = useCallback(async () => {
    if (!channelId) return
    setSaving(true)
    setActionError('')
    setSuccess('')
    try {
      if (mode === 'BLACK') {
        saveResult(await adTransitionApi.useBlack(channelId, blackDurationMs))
      } else {
        saveResult(await adTransitionApi.useVideo(channelId, selectedMedia?.absolutePath))
      }
    } catch (err: any) {
      setActionError(err?.message ?? 'Enregistrement impossible')
    } finally {
      setSaving(false)
    }
  }, [blackDurationMs, channelId, mode, saveResult, selectedMedia])

  const locked = !canManage || !settings?.canEdit || saving
  const videoReady = Boolean(selectedMedia || settings?.videoFilePath)

  return (
    <section id="ads-tab-panel" role="tabpanel" aria-labelledby="ads-tab-transitions" className="ad-transition-settings">
      <header className="ad-transition-settings__header">
        <div>
          <h2>Transition entre les publicités</h2>
          <p>Ce choix est relu par le moteur au lancement de chaque bloc publicitaire.</p>
        </div>
        {settings && (
          <span className="ad-transition-settings__status">
            Actif : {settings.mode === 'BLACK'
              ? `écran noir · ${formatMilliseconds(settings.blackDurationMs)}`
              : `vidéo · ${settings.videoFileName}`}
          </span>
        )}
      </header>

      <div className="ad-transition-warning" role="note">
        <AlertTriangle aria-hidden="true" />
        <div>
          <strong>Ce réglage modifie la durée des blocs déjà placés.</strong>
          <span>Il peut bouleverser les écarts entre les programmes, les blocs déjà programmés sur la grille et ceux issus des modèles de grille.</span>
        </div>
      </div>

      {settings && !settings.canEdit && (
        <div className="tp-feedback tp-feedback--warning" role="status">
          {settings.blockedReason}
          {settings.blockedUntil && ` Réessayez après ${new Date(settings.blockedUntil).toLocaleTimeString('fr-FR')}.`}
        </div>
      )}
      {(loadError || actionError) && <div className="tp-feedback tp-feedback--error" role="alert">{actionError || loadError}</div>}
      {success && <div className="tp-feedback tp-feedback--success" role="status">{success}</div>}

      {loading ? (
        <div className="tp-empty-state"><Spinner /> Chargement…</div>
      ) : settings ? (
        <div className="ad-transition-settings__content">
          <div className="ad-transition-settings__choices" aria-label="Type de transition">
            <button
              type="button"
              className={`ad-transition-choice${mode === 'BLACK' ? ' is-selected' : ''}`}
              aria-pressed={mode === 'BLACK'}
              onClick={handleModeBlack}
              disabled={locked}
            >
              <Monitor aria-hidden="true" />
              <span><strong>Écran noir</strong><small>Durée réglable en millisecondes</small></span>
            </button>
            <button
              type="button"
              className={`ad-transition-choice${mode === 'VIDEO' ? ' is-selected' : ''}`}
              aria-pressed={mode === 'VIDEO'}
              onClick={handleModeVideo}
              disabled={locked}
            >
              <Film aria-hidden="true" />
              <span><strong>Média vidéo</strong><small>Le média complet sera joué entre deux spots</small></span>
            </button>
          </div>

          {mode === 'BLACK' ? (
            <label className="ad-transition-field">
              <span>Durée de l’écran noir</span>
              <span className="ad-transition-field__control">
                <input
                  type="number"
                  min={0}
                  max={60_000}
                  step={50}
                  value={blackDurationMs}
                  onChange={handleDurationChange}
                  disabled={locked}
                  className="tp-input"
                />
                <span>ms</span>
              </span>
              <small>De 0 à 60 000 ms. Une valeur de 0 enchaîne les spots sans coupure.</small>
            </label>
          ) : (
            <div className="ad-transition-field">
              <span>Vidéo de transition</span>
              <button type="button" className="tp-button" onClick={openBrowser} disabled={locked}>
                Choisir dans le Media Browser
              </button>
              <small>
                {selectedMedia
                  ? `Prêt à utiliser : ${selectedMedia.fileName} · ${formatMilliseconds(selectedMedia.durationMs ?? (selectedMedia.durationSec ?? 0) * 1000)}`
                  : settings.videoFilePath
                    ? `Média confirmé : ${settings.videoFileName} · ${formatMilliseconds(settings.videoDurationMs)}`
                    : 'Sélectionnez une vidéo dans le Media Browser puis confirmez son utilisation.'}
              </small>
            </div>
          )}

          <div className="ad-transition-settings__footer">
            <span>Les modifications sont possibles uniquement hors période publicitaire.</span>
            <button
              type="button"
              className="tp-button tp-button--primary"
              onClick={handleSave}
              disabled={locked || (mode === 'VIDEO' && !videoReady)}
            >
              {saving ? 'Application…' : mode === 'VIDEO' && selectedMedia ? 'Confirmer ce média' : 'Appliquer à la grille'}
            </button>
          </div>
        </div>
      ) : null}
      {browserOpen && <MediaBrowserModal onSelect={handleMediaSelect} onClose={closeBrowser} />}
    </section>
  )
})
